import type { ComputedRef, WritableComputedRef } from "vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { Maybe, ModioMod } from "@/graphql";
import type { IUseMods } from "@/components/views/mods";

/**
 * Base mod.io mod interface for games whose servers download mods from mod.io themselves —
 * we only manage the list of mod ids (see the Deadside console game profile).
 * @param selectedMods The full currently selected mods, as resolved by the backend
 * @param modIds The currently selected mod ids (order-preserving)
 */
export function useModioMods(
  selectedMods: ComputedRef<Maybe<ModioMod>[]>,
  modIds: WritableComputedRef<string[]>,
): IUseMods<ModioMod> {
  const { t } = useI18n();
  const storeTitle = computed(() => t("ngp/mods/dialog/link/modio"));

  const installedMods = computed(
    (): Array<ModioMod> =>
      modIds.value
        .map((modId: string) => selectedMods.value.find((mod: Maybe<ModioMod>) => mod?.id.toString() == modId))
        .filter((mod): mod is ModioMod => mod != null),
  );

  function addMod(mod: ModioMod) {
    if (modIds.value.includes(mod.id.toString())) return;
    modIds.value.push(mod.id.toString());
    // Predictive UI - the backend resolves the list on reload, this makes it look instant :)
    selectedMods.value.push(mod);
  }

  function removeMod(mod: ModioMod) {
    const index = modIds.value.indexOf(mod.id.toString());
    if (index > -1) {
      modIds.value.splice(index, 1);
    }
  }

  // Hide game-version tags (e.g. "1.2.1.5") on the mod cards for now — Core Keeper tags every mod
  // with all compatible game versions, and we don't know yet how Deadside will use its taxonomy
  const versionTagPattern = /^v?\d+(\.\d+)*$/;

  function modTags(mod: ModioMod) {
    return (mod.tags ?? []).filter((tag): tag is string => !!tag && !versionTagPattern.test(tag));
  }

  return { storeTitle, installedMods, addMod, removeMod, modTags };
}
